import React from 'react';
import { TabsContent } from '../ui/tabs';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Link } from 'react-router-dom';
import { TrendingUp, Users, DollarSign, FileText } from 'lucide-react';
import { CurrencyDisplay } from '../ui/currency-display';

const formatCurrency = (amount) => {
  if (amount == null || isNaN(amount)) return '$0';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
};

export function SummaryTab({
  project, financialSummary, roiData, waterfallData,
  jvAgreements, owners, profitAllocation, transactions,
  daysMetric, isOwner, isSold, hasJV,
  costBasis, totalExpenses, grossProfit, landFinderProfit,
}) {
  if (project?.is_subdivide_master) {
    return (
      <TabsContent value="summary">
        <Card className="rounded-sm">
          <CardContent className="py-12 text-center space-y-2">
            <FileText className="w-8 h-8 mx-auto text-muted-foreground" />
            <p className="text-muted-foreground max-w-md mx-auto">
              This is a <strong>Subdivide Master</strong> deal. Its financials — cap table,
              lot matrix, and per-closing payouts — live on the <strong>Lots &amp; Closings</strong> tab
              (the single, accurate methodology for staggered lot sales).
            </p>
          </CardContent>
        </Card>
      </TabsContent>
    );
  }

  if (!isSold) return null;

  return (
    <TabsContent value="summary">
      <div className="space-y-6">
        {financialSummary ? (
          <>
            {/* Gross Profit Calculation */}
            <Card className="rounded-sm border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Deal Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="p-4 bg-white rounded-sm border">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Sale Price</p>
                    <CurrencyDisplay amount={financialSummary.profit_overview?.sale_price || 0} size="xl" className="font-heading text-emerald-600" />
                  </div>
                  <div className="p-4 bg-white rounded-sm border">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Total Cost Basis</p>
                    <CurrencyDisplay amount={-(financialSummary.cost_basis_breakdown?.total_cost_basis || 0)} size="xl" className="font-heading text-red-600" />
                  </div>
                  <div className="p-4 bg-white rounded-sm border">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Shared Expenses</p>
                    <CurrencyDisplay amount={-(financialSummary.transactions_summary?.shared_expenses || 0)} size="xl" className="font-heading text-amber-600" />
                  </div>
                  <div className="p-4 bg-white rounded-sm border">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Gross Profit</p>
                    <CurrencyDisplay amount={
                      (financialSummary.profit_overview?.sale_price || 0)
                      - (financialSummary.cost_basis_breakdown?.total_cost_basis || 0)
                      - (financialSummary.transactions_summary?.shared_expenses || 0)
                    } size="xl" className="font-heading text-forest-600" />
                  </div>
                  <div className="p-4 bg-muted/30 rounded-sm">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Days Held</p>
                    <p className="text-xl font-heading font-bold font-mono">{financialSummary?.project?.days_held ?? daysMetric.days}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Note Receivable Summary (for Note Held projects) */}
            {financialSummary.is_note_held && financialSummary.note_info && (
              <Card className="rounded-sm border border-orange-200" data-testid="note-receivable-summary">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
                    <FileText className="w-4 h-4 text-emerald-600" />
                    Note Receivable
                    <Badge variant="outline" className="text-[10px] bg-orange-50 text-orange-700 border-orange-200 rounded-sm ml-auto">
                      {financialSummary.note_info.status}
                    </Badge>
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">
                    Sale: {formatCurrency((financialSummary.note_info.principal_amount || 0) + (financialSummary.note_info.down_payment || 0))}
                    {financialSummary.note_info.down_payment > 0 && (
                      <span> &middot; Down Payment: {formatCurrency(financialSummary.note_info.down_payment)}</span>
                    )}
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="p-3 bg-white rounded-sm border">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Principal</p>
                      <p className="text-lg font-heading font-bold font-mono text-orange-700">
                        {formatCurrency(financialSummary.note_info.principal_amount)}
                      </p>
                    </div>
                    <div className="p-3 bg-white rounded-sm border">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Interest Rate</p>
                      <p className="text-lg font-heading font-bold font-mono text-emerald-600">
                        {financialSummary.note_info.interest_rate}%
                      </p>
                    </div>
                    <div className="p-3 bg-white rounded-sm border">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Monthly Payment</p>
                      <p className="text-lg font-heading font-bold font-mono text-purple-700">
                        {formatCurrency(financialSummary.note_info.monthly_payment)}
                      </p>
                    </div>
                    <div className="p-3 bg-muted/30 rounded-sm">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Term</p>
                      <p className="text-lg font-heading font-bold font-mono">
                        {financialSummary.note_info.term_months} mo
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="p-3 rounded-sm border">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Buyer</p>
                      <p className="text-sm font-semibold truncate">{financialSummary.note_info.buyer_name}</p>
                    </div>
                    <div className="p-3 bg-white rounded-sm border">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Payments Received</p>
                      <p className="text-lg font-heading font-bold font-mono text-emerald-600">
                        {formatCurrency((financialSummary.note_info.total_received || 0) - (financialSummary.note_info.down_payment || 0))}
                      </p>
                    </div>
                    <div className="p-3 bg-white rounded-sm border">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Remaining Balance</p>
                      <p className="text-lg font-heading font-bold font-mono text-red-600">
                        {formatCurrency(financialSummary.note_info.remaining_balance)}
                      </p>
                    </div>
                  </div>

                  {financialSummary.note_info.remaining_balance > 0 && (
                    <div className="mt-4">
                      <div className="flex justify-between text-xs text-muted-foreground mb-1">
                        <span>Payment Progress</span>
                        <span>
                          {Math.round(((financialSummary.note_info.total_received - (financialSummary.note_info.down_payment || 0)) / financialSummary.note_info.principal_amount) * 100)}%
                        </span>
                      </div>
                      <Progress
                        value={((financialSummary.note_info.total_received - (financialSummary.note_info.down_payment || 0)) / financialSummary.note_info.principal_amount) * 100}
                        className="h-2"
                      />
                    </div>
                  )}

                  <div className="mt-3 pt-3 border-t">
                    <Link to={`/notes?project_id=${project.id}`}>
                      <button className="inline-flex items-center justify-center rounded-sm text-xs border border-input bg-background hover:bg-accent hover:text-accent-foreground h-8 px-3" data-testid="view-note-details-btn">
                        <FileText className="w-3.5 h-3.5 mr-1.5" />
                        View Full Note Details
                      </button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ROI/IRR Metrics Card */}
            {roiData?.metrics && (
              <Card className="rounded-sm border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Return on Investment
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-white rounded-sm border">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Total Investment</p>
                      <p className="text-xl font-heading font-bold font-mono text-blue-600">
                        {formatCurrency(roiData.metrics.total_investment)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">Purchase + Expenses</p>
                    </div>
                    <div className="p-4 bg-white rounded-sm border">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Net Profit</p>
                      <CurrencyDisplay amount={roiData.metrics.profit} size="xl" className="font-heading text-emerald-600" />
                      <p className="text-xs text-muted-foreground mt-1">Sale - Investment</p>
                    </div>
                    <div className="p-4 bg-white rounded-sm border">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">ROI</p>
                      <p className={`text-2xl font-heading font-bold font-mono ${roiData.metrics.roi_percentage >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {roiData.metrics.roi_percentage >= 0 ? '+' : ''}{roiData.metrics.roi_percentage.toFixed(1)}%
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">Return on Investment</p>
                    </div>
                    <div className="p-4 bg-white rounded-sm border">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Annualized ROI</p>
                      <p className={`text-2xl font-heading font-bold font-mono ${roiData.metrics.annualized_roi >= 0 ? 'text-forest-600' : 'text-red-600'}`}>
                        {roiData.metrics.annualized_roi >= 0 ? '+' : ''}{roiData.metrics.annualized_roi.toLocaleString('en-US', { maximumFractionDigits: 1 })}%
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">{roiData.metrics.days_held} days held</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Investor Summary */}
            {financialSummary.investor_summary?.total_investors > 0 && (
              <Card className="rounded-sm border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Investor Summary ({financialSummary.investor_summary.total_investors} investor{financialSummary.investor_summary.total_investors > 1 ? 's' : ''})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {financialSummary.investor_summary.investors?.map((investor, idx) => (
                    <div key={investor.jv_id || idx} className={`${idx > 0 ? 'mt-6 pt-6 border-t' : ''}`}>
                      <p className="font-semibold text-sm mb-3 text-emerald-600">{investor.investor_name}</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                        <div className="p-2 sm:p-3 bg-white rounded-sm border" data-testid={`investor-capital-${idx}`}>
                          <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground">Capital Invested</p>
                          <p className="text-sm sm:text-lg font-heading font-bold font-mono">{formatCurrency(investor.capital_contributed)}</p>
                        </div>
                        <div className="p-2 sm:p-3 bg-white rounded-sm border" data-testid={`investor-expenses-${idx}`}>
                          <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground">Expenses Paid</p>
                          <p className="text-sm sm:text-lg font-heading font-bold font-mono text-red-600">{formatCurrency(investor.expenses_paid_by_investor)}</p>
                        </div>
                        {investor.reimbursements_received > 0 && (
                          <div className="p-2 sm:p-3 bg-white rounded-sm border" data-testid={`investor-refunds-${idx}`}>
                            <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground">Refunds Received</p>
                            <p className="text-sm sm:text-lg font-heading font-bold font-mono text-emerald-600">{formatCurrency(investor.reimbursements_received)}</p>
                          </div>
                        )}
                        <div className="p-2 sm:p-3 bg-white rounded-sm border" data-testid={`investor-cost-basis-${idx}`}>
                          <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground">Cost Basis</p>
                          <p className="text-sm sm:text-lg font-heading font-bold font-mono">{formatCurrency(investor.total_investor_cost_basis)}</p>
                        </div>
                        <div className="p-2 sm:p-3 bg-white rounded-sm border" data-testid={`investor-profit-${idx}`}>
                          <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground">Profit ({investor.investor_split_percentage}%)</p>
                          <p className="text-sm sm:text-lg font-heading font-bold font-mono text-emerald-600">{formatCurrency(investor.investor_profit_share)}</p>
                          <p className="text-[10px] sm:text-xs text-muted-foreground">Tier {investor.active_tier}</p>
                        </div>
                        <div className="p-2 sm:p-3 bg-white rounded-sm border border-foreground/20 col-span-2 sm:col-span-1" data-testid={`investor-total-return-${idx}`}>
                          <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground">Total Return</p>
                          <p className="text-base sm:text-xl font-heading font-bold font-mono">{formatCurrency(investor.total_return_to_investor)}</p>
                        </div>
                      </div>
                      {project.status === 'Sold' && (investor.cash_on_cash_return !== undefined || investor.annualized_roi !== undefined) && (
                        <div className="grid grid-cols-2 gap-3 mt-3">
                          <div className="p-2 sm:p-3 bg-white rounded-sm border" data-testid={`investor-coc-${idx}`}>
                            <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground">Cash on Cash Return</p>
                            <p className={`text-sm sm:text-lg font-heading font-bold font-mono ${(investor.cash_on_cash_return || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {(investor.cash_on_cash_return || 0) >= 0 ? '+' : ''}{(investor.cash_on_cash_return || 0).toFixed(2)}%
                            </p>
                            <p className="text-[10px] text-muted-foreground">Profit / Invested Capital</p>
                          </div>
                          <div className="p-2 sm:p-3 bg-white rounded-sm border" data-testid={`investor-annualized-roi-${idx}`}>
                            <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground">Annualized ROI</p>
                            <p className={`text-sm sm:text-lg font-heading font-bold font-mono ${(investor.annualized_roi || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {(investor.annualized_roi || 0) >= 0 ? '+' : ''}{(investor.annualized_roi || 0).toFixed(2)}%
                            </p>
                            <p className="text-[10px] text-muted-foreground">{investor.days_held} days held</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Land Finder Returns */}
            <Card className="rounded-sm border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Land Finder Returns
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="p-3 bg-white rounded-sm border">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Profit Share ({financialSummary.investor_summary?.investors?.[0] ? (100 - financialSummary.investor_summary.investors[0].investor_split_percentage) : 100}%)</p>
                    <p className="text-lg font-heading font-bold font-mono text-emerald-600">
                      {formatCurrency(financialSummary.land_finder_summary?.land_finder_profit_share || 0)}
                    </p>
                  </div>
                  {isOwner && (
                    <div className="p-3 bg-white rounded-sm border">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">- Commissions</p>
                      <p className="text-lg font-heading font-bold font-mono text-red-600">
                        -{formatCurrency(financialSummary.commission_summary?.total_commissions || 0)}
                      </p>
                    </div>
                  )}
                  <div className={`p-3 bg-white rounded-sm border ${isOwner ? 'md:col-span-2' : ''}`}>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">= LF Profit</p>
                    <p className="text-2xl font-heading font-bold font-mono text-forest-600">
                      {formatCurrency(financialSummary.land_finder_summary?.net_to_owners || 0)}
                    </p>
                  </div>
                </div>
                {(financialSummary.land_finder_summary?.land_finder_cost_basis || 0) > 0 && (
                  <p className="text-xs text-muted-foreground">
                    LF Capital Contributed: {formatCurrency(financialSummary.land_finder_summary?.land_finder_cost_basis || 0)} (returned separately, not included in profit)
                  </p>
                )}
                
                {/* Owner Profit Preview */}
                {isOwner && owners.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground mb-3">Owner Profit Preview</p>
                    <div className="space-y-2">
                      {owners.filter(o => o.is_active).map((owner, idx) => {
                        const ownerAmount = (financialSummary.land_finder_summary?.net_to_owners || 0) * owner.ownership_percentage;
                        const percentage = (owner.ownership_percentage * 100).toFixed(0);
                        return (
                          <div key={owner.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-sm">
                            <div className="flex items-center gap-3">
                              <div className="w-2 h-8 rounded-full bg-forest-500" style={{ opacity: 1 - (idx * 0.2) }} />
                              <div>
                                <p className="font-medium text-sm">{owner.name}</p>
                                <p className="text-xs text-muted-foreground">{percentage}% ownership</p>
                              </div>
                            </div>
                            <p className="text-lg font-mono font-bold text-forest-600">{formatCurrency(ownerAmount)}</p>
                          </div>
                        );
                      })}
                      <div className="flex justify-between items-center pt-2 mt-2 border-t text-sm">
                        <span className="text-muted-foreground">Total Profit</span>
                        <span className="font-mono font-semibold">{formatCurrency(financialSummary.land_finder_summary?.net_to_owners || 0)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Commission Details */}
            {isOwner && financialSummary.commission_summary?.commission_entries?.length > 0 && (
              <Card className="rounded-sm border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold uppercase tracking-wide">Commission Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {financialSummary.commission_summary.commission_entries.map((comm, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2 border-b last:border-0">
                        <span className="text-sm">{comm.employee_name}</span>
                        <span className="font-mono font-medium text-red-600">-{formatCurrency(comm.amount)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <>
            {/* Fallback display */}
            <Card className="rounded-sm border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Profit Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-muted/30 rounded-sm">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Sale Price</p>
                    <p className="text-xl font-heading font-bold font-mono">{formatCurrency(project.sale_price)}</p>
                  </div>
                  <div className="p-4 bg-muted/30 rounded-sm">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Total Cost Basis</p>
                    <p className="text-xl font-heading font-bold font-mono text-red-600">
                      -{formatCurrency((project.purchase_price || 0) + totalExpenses)}
                    </p>
                    <p className="text-xs text-muted-foreground">Purchase + Expenses</p>
                  </div>
                  <div className="p-4 bg-white rounded-sm border">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Gross Profit</p>
                    <p className="text-xl font-heading font-bold font-mono text-emerald-600">{formatCurrency(grossProfit)}</p>
                  </div>
                  <div className="p-4 bg-muted/30 rounded-sm">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Days Held</p>
                    <p className="text-xl font-heading font-bold font-mono">{waterfallData?.days_held ?? daysMetric.days}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {hasJV && waterfallData && (
              <Card className="rounded-sm border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Investor Returns
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="p-4 bg-white rounded-sm border">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Total Capital At Risk</p>
                      <p className="text-lg font-heading font-bold font-mono">{formatCurrency(waterfallData?.investor_cost_basis ?? jvAgreements[0]?.capital_contributed)}</p>
                      {waterfallData?.investor_expenses > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          JV: {formatCurrency(waterfallData?.jv_capital)} + Expenses: {formatCurrency(waterfallData?.investor_expenses)}
                        </p>
                      )}
                    </div>
                    <div className="p-4 bg-muted/30 rounded-sm">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Capital Returned</p>
                      <p className="text-lg font-heading font-bold font-mono">{formatCurrency(waterfallData.capital_returned)}</p>
                    </div>
                    <div className="p-4 bg-white rounded-sm border">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Investor Profit</p>
                      <p className="text-lg font-heading font-bold font-mono text-emerald-600">{formatCurrency(waterfallData.investor_profit)}</p>
                    </div>
                    <div className="p-4 bg-muted/30 rounded-sm">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Investor Split</p>
                      <p className="text-lg font-heading font-bold font-mono">{waterfallData.investor_split_percentage}%</p>
                      <p className="text-xs text-muted-foreground">Tier {waterfallData.active_tier}</p>
                    </div>
                    <div className="p-4 bg-white rounded-sm border">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Total to Investor</p>
                      <p className="text-lg font-heading font-bold font-mono text-blue-600">
                        {formatCurrency(waterfallData.capital_returned + waterfallData.investor_profit)}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">Investor: {jvAgreements[0]?.investor_name}</p>
                </CardContent>
              </Card>
            )}

            <Card className="rounded-sm border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Land Finder Returns
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="p-4 bg-white rounded-sm border">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Land Finder Profit</p>
                    <p className="text-2xl font-heading font-bold font-mono text-forest-500">{formatCurrency(landFinderProfit)}</p>
                    {hasJV && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {100 - (waterfallData?.investor_split_percentage || 0)}% of distributable profit
                      </p>
                    )}
                  </div>
                  {isOwner && profitAllocation && (
                    <>
                      <div className="p-4 bg-white rounded-sm border">
                        <p className="text-xs uppercase tracking-wide text-muted-foreground">Distributed to Owners</p>
                        <p className="text-2xl font-heading font-bold font-mono text-emerald-600">{formatCurrency(profitAllocation.distribution_amount)}</p>
                      </div>
                      <div className="p-4 bg-white rounded-sm border">
                        <p className="text-xs uppercase tracking-wide text-muted-foreground">Reinvested</p>
                        <p className="text-2xl font-heading font-bold font-mono text-blue-600">{formatCurrency(profitAllocation.reinvestment_amount)}</p>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </TabsContent>
  );
}
